import { FC, Fragment, useCallback, useEffect, useRef, useState, useContext } from "react";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { getProfessionals, GetProfessionalsParams, IProfessional, IProfessionalSchedulesAvailable, TipoProfissional } from "../api/getProfessionals";
import registerCheckout from "../api/registerCheckout";
import ButtonSecondary from "../components/buttons/ButtonSecondary";
import CardTimesProfessionals from "../components/cards/CardTimesProfessionals";
import PageWrapper from "../components/common/PageWrapper";
import CurrentFilters from "../components/CurrentFilters";
import SearchField from "../components/fields/SearchField";
import { FilterIcon } from "../components/Icons";
import LazyLoad from "../components/LazyLoad";
import CardHorariosLoader from "../components/loaders/CardHorariosLoader";
import { SubTitle } from "../components/Titles";
import getUserData from "../components/utils/getUserData";
import ChooseProcedureModal from "../modals/ChooseProcedureModal";
import FiltersProfessionalsModal from "../modals/FiltersProfessionalModal";
import { isValidDate } from "../utils/date-util/dateUtils";
import { debounce } from "../utils/debounce";
import getSchedulesOfTime from "../utils/getProceduresOfTime";
import haveFilters from "../utils/verifyHaveFilters";
import { getUserToken } from "../common/axios/axiosConfig";
import ThemeContext from "../themes/context";

const ContentArea = styled.div`
	padding-left: 25px;
	padding-right: 25px;
`;

const FilterButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-end;
	align-items: center;
	align-content: flex-start;
	padding-right: 22px;
	padding-top: 17px;
	button {
		color: ${"#212121"} !important;
		background-color: ${props => (props.theme.colors.lightGrayVitat)
	};
`;

const ScreenSeparator = styled.div`
	width: 100%;
	min-height: 1px;
	background-color: ${props => (props.theme.colors.lightGrayVitat || "f6f2f8")};
`;

const ResultsNotFound = styled.div`
	width: 100%;
	height: 100%;
	color: #6f7590;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: center;
	align-items: center;
	align-content: normal;
	* {
		margin: 0px;
		padding: 0px;
	}
	h3 {
		font-family: "Livvic";
		font-style: normal;
		font-weight: 400;
		font-size: 15px;
		line-height: 150%;
	}
	h4 {
		font-family: "Livvic";
		font-style: normal;
		font-weight: 400;
		font-size: 13px;
		line-height: 150%;
	}
`;

const InsidePageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
`;

const ProfessionalsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
	overflow: auto;
`;

const TimeZone = styled.div`
  font-family: Livvic;
  font-size: 12px;
  text-align: right;
  color: #6f7590;
  margin-top: 10px;
  padding: 0 25px;
`;

const SuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column; 
`;

const SubTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

interface ModalInfo {
	schedule: IProfessionalSchedulesAvailable;
	availableSchedules: IProfessionalSchedulesAvailable[];
	professional: IProfessional;
}

/**
 * ### Tela de busca de profissionais
 * Mostra uma listagem de profissionais, suas specialtys e em quais hoários ele esta disponível.
 */
const ProfessionalsScreen: FC = () => {
	const [isModalFiltroOpen, setIsModalFiltroOpen] = useState<boolean>(false);
	const [pageFilters, setPageFilters] = useState<GetProfessionalsParams>({});
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [professionals, setProfessionals] = useState<IProfessional[] | undefined>();
	const [hasNextPage, setHasNextPage] = useState<boolean>(true);
	const [isNextPageLoading, setIsNextPageLoading] = useState<boolean>(false);
	const [searchFieldValue, setSearchFieldValue] = useState<string>(searchParams.get("search") || "");
	const searchInput = useRef<HTMLInputElement>(null);
	const offset = useRef(0);
	const itemsPerPage = 10;
	const theme = useContext(ThemeContext);

	// Controle fila de request - FILO (First In Last Out)
	const requestRef = useRef(0);
	const [queue] = useState<number[]>([]);

	// Controle primeiroLoad da página
	const firstLoad = useRef(0);

	// Controle exibir modal para escolher procedimento
	const [isChooseModalOpen, setIsChooseModalOpen] = useState<boolean>(false);
	const [modalInfo, setModalInfo] = useState<ModalInfo>();

	useEffect(() => {
		/* 
			CanGoBack é adicionado aos queryParams após ter realizado primeiro load, portanto,
			buscar apenas se for no primeiro load ou se ainda não tiver adicionado canGoBack aos queryParams da rota
		*/
		if (!searchParams.get("canGoBack") || firstLoad.current === 0) {
			setIsNextPageLoading(true);
			carregaDados(requestRef.current, false);
			firstLoad.current += 1;
		}

		const focusSearch = searchParams.get("focus");
		if (focusSearch && searchInput) searchInput.current?.focus();

		return () => {
			setHasNextPage(false);
		};
	}, [searchParams]);

	const getCurrentFilters = (): GetProfessionalsParams => {
		const pageFilter: GetProfessionalsParams = pageFilters;

		const priceUrl = searchParams.get("price");
		const dataFinalUrl = searchParams.get("dataFinal");
		const ratingUrl = searchParams.get("rating");
		const searchUrl = searchParams.get("search");
		const tipoProfissional = searchParams.get("tipoProfissional");

		try {
			if (priceUrl && priceUrl !== "null") {
				pageFilter.price = parseFloat(priceUrl);
			} else {
				pageFilter.price = undefined;
			}
		} catch (err) {
			pageFilter.price = undefined;
		}

		try {
			if (dataFinalUrl && dataFinalUrl !== "null") {
				if (isValidDate(dataFinalUrl)) {
					pageFilter.dataFinal = dataFinalUrl;
				} else {
					pageFilter.dataFinal = undefined;
				}
			} else {
				pageFilter.dataFinal = undefined;
			}
		} catch (err) {
			pageFilter.dataFinal = undefined;
		}

		try {
			if (ratingUrl && ratingUrl !== "null") {
				pageFilter.rating = ratingUrl;
			} else {
				pageFilter.rating = undefined;
			}
		} catch (err) {
			pageFilter.rating = undefined;
		}

		if (searchUrl && searchUrl !== "null") {
			pageFilter.search = searchUrl;
		} else {
			pageFilter.search = undefined;
		}

		try {
			if (tipoProfissional && tipoProfissional !== "null") {
				const tiposProfissionais = ["clinico_geral", "nutricionista", "personal_trainer", "psicologo"];
				pageFilter.tipoProfissional = tiposProfissionais.includes(tipoProfissional) ? (tipoProfissional as TipoProfissional) : undefined;
			} else {
				pageFilter.tipoProfissional = undefined;
			}
		} catch (err) {
			pageFilter.tipoProfissional = undefined;
		}

		return pageFilter;
	};

	//exemplo de aplicação de filters (posteriormente vai fazer funcionar no modal de filters)
	const aplicaFiltros = (filters: GetProfessionalsParams) => {
		const paramsJson: Record<string, string> = {};

		if (filters.price) paramsJson.price = filters.price.toString();
		if (filters.dataFinal) paramsJson.dataFinal = filters.dataFinal;
		if (filters.rating) paramsJson.rating = filters.rating;
		if (filters.search) paramsJson.search = filters.search.toString();
		if (filters.specialty) paramsJson.specialty = filters.specialty.toString();
		if (filters.tipoProfissional) paramsJson.tipoProfissional = filters.tipoProfissional.toString();

		setSearchFieldValue(paramsJson.search);

		const searchParams = createSearchParams(paramsJson).toString();

		offset.current = 0;
		setProfessionals(undefined);
		setIsNextPageLoading(true);

		navigate("/medicos?" + searchParams, {
			replace: true,
		});
	};

	const carregaDados = async (ref: number, concatenate = true) => {
		// Adiciona na primeira posição da fila a referencia da atual request e incrementa o numero da proxima referencia;
		queue.unshift(ref);
		requestRef.current += 1;

		const filters: GetProfessionalsParams = getCurrentFilters();
		setPageFilters(filters);

		const itemsOffset = offset.current * itemsPerPage;
		try {
			await getUserToken();

			const userData = getUserData();

			const data = await getProfessionals(itemsOffset, itemsPerPage, {
				...filters,
				procedureIds: userData.procedureId ? JSON.stringify([userData.procedureId]) : undefined,
			});

			const hasMore = professionals ? professionals.length < data.tamanho : true;
			setHasNextPage(hasMore);
			if (hasMore) offset.current += 1;

			if (queue[0] === ref) {
				// Parar de exibir loading apenas quando ultima request
				setIsNextPageLoading(false);

				if (concatenate) {
					if (data.profissionais.length === 0) {
						setIsNextPageLoading(false);
						setHasNextPage(false);
						return;
					}

					setProfessionals(prevProfessionals => {
						if (!prevProfessionals) return data.profissionais;

						return [...prevProfessionals, ...data.profissionais];
					});
				} else setProfessionals(data.profissionais);
			}
		} catch (error) {
			setProfessionals([]);
			setIsNextPageLoading(false);
		} finally {
			// Remove o ultimo elemento da fila
			queue.pop();
		}
	};

	const loadNextPage = () => {
		if (!isNextPageLoading) {
			setIsNextPageLoading(true);
			carregaDados(requestRef.current);
		}
	};

	const aplicaFiltrosHandler = useCallback(debounce(aplicaFiltros, 300), []);
	const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		const search = event.target.value;
		setSearchFieldValue(search);
		aplicaFiltrosHandler({
			...pageFilters,
			search,
		});
	};

	// MULTIPROCEDIMENTO
	const handleAvailableTimeClick = async (availableSchedule: IProfessionalSchedulesAvailable, professional: IProfessional) => {
		const userData = getUserData();
		if (!userData.procedureId) {
			const schedules = getSchedulesOfTime(availableSchedule.horarioInicial, professional.horariosDisponiveis);
			setModalInfo({
				schedule: availableSchedule,
				availableSchedules: schedules,
				professional,
			});
			setIsChooseModalOpen(true);
			return;
		}

		const response = await registerCheckout(availableSchedule.idHorario);
		if (response) {
			navigate(`/medicos/${professional.codigoProfissional}/checkout/${response.id}?step=0`, { state: availableSchedule });
		}
	};

	const handleCloseModalChooseProcedure = () => {
		setModalInfo(undefined);
		setIsChooseModalOpen(false);
	};

	return (
		<PageWrapper>
			<InsidePageWrapper>
				<div style={{ width: "100%", minHeight: "30px" }} /> {/*Espaçamento*/}
				<ContentArea>
					<SearchField placeholder="Pesquise aqui" onChange={onChangeSearch} value={searchFieldValue || ""} inputRef={searchInput} />
				</ContentArea>
				<FilterButtonWrapper>
					<ButtonSecondary
						icon={<FilterIcon color={theme.colors.blackVitat || "#002B49"} size={14} />}
						label="Filtrar"
						onClick={() => {
							setIsModalFiltroOpen(true);
						}}
					/>
				</FilterButtonWrapper>

				<SuggestionsContainer>
					{haveFilters() ? (
						<CurrentFilters handleChangeFilters={aplicaFiltros} filters={pageFilters} />
					) : (
						<SubTitleContainer>
							<SubTitle>Sugestões</SubTitle>
							<TimeZone>Horário de Brasília GMT-3</TimeZone>
						</SubTitleContainer>
					)}
				</SuggestionsContainer>

				<ScreenSeparator />
				<ProfessionalsWrapper>
					{!professionals && (
						<>
							<CardHorariosLoader />
							<CardHorariosLoader />
							<CardHorariosLoader />
						</>
					)}

					{professionals && (
						<>
							{!isNextPageLoading && professionals.length === 0 ? (
								<ResultsNotFound>
									<h3>Nenhum resultado encontrado.</h3>
									<h4>Altere os filtros e tente novamente</h4>
								</ResultsNotFound>
							) : (
								<LazyLoad
									customStyles={{ alignItems: "left" }}
									SkeletonComponent={CardHorariosLoader}
									hasNextPage={hasNextPage}
									loadNextPage={loadNextPage}
									skeletonNumber={3}
								>
									{professionals.map(professional => (
										<Fragment key={professional.codigoProfissional}>
											<CardTimesProfessionals
												professional={professional}
												onAvailableTimeClick={availableSchedule => handleAvailableTimeClick(availableSchedule, professional)}
											/>
											<ScreenSeparator />
										</Fragment>
									))}
								</LazyLoad>
							)}
						</>
					)}
				</ProfessionalsWrapper>
				<FiltersProfessionalsModal
					isOpen={isModalFiltroOpen}
					onChange={(filters: GetProfessionalsParams) => {
						setIsModalFiltroOpen(false);
						aplicaFiltros(filters);
					}}
					values={pageFilters}
				/>
			</InsidePageWrapper>
			{modalInfo && (
				<ChooseProcedureModal
					isOpen={isChooseModalOpen}
					handleClose={handleCloseModalChooseProcedure}
					scheduleInfo={{
						professional: modalInfo.professional.nomeProfissional,
						time: modalInfo.schedule.horarioInicial,
					}}
					availableSchedules={modalInfo.availableSchedules}
					professional={modalInfo.professional}
				/>
			)}
		</PageWrapper>
	);
};

export default ProfessionalsScreen;
